import { Enums, Interfaces } from '@configur-tech/upit-core-types';
import { cloneDeep } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GroupItem } from '../../components/Aggregation/AggregationGroup';
import { SortItem } from '../../components/Aggregation/AggregationSort';
import { DatasetMetaItemOutput } from '../../services/dataset-meta/DatasetMetaService';
import { fetchGraphSuccess } from '../../store/graph';
import { RootState } from '../../store/rootReducer';
import useFilter from '../filter/UseFilter';
import useGraph from '../graph/UseGraph';

const INITIAL_QUERY_OPTIONS = {
  fieldItems: [],
  groupItems: [],
  sortItems: [],
  displayOrderItems: [],
  listIds: [],
};

export interface GraphFieldWithDsmName extends Interfaces.QueryField {
  datasetMetaName: string;
}

export interface GraphOptions {
  fieldItems: GraphFieldWithDsmName[];
  groupItems: GroupItem[];
  sortItems: SortItem[];
  displayOrderItems: SortItem[];
  listIds: string[];
}

interface useGraphBuilderResult {
  graphOptions: GraphOptions;
  // Helpers
  handleGroupChange: (items: GroupItem[]) => void;
  handleSortChange: (items: SortItem[]) => void;
  handleSubtotalChange: (checked: boolean) => void;
  handleTotalChange: (checked: boolean) => void;
  handleDisplayOrderChange: (items: SortItem[]) => void;
  // Sorters
  sortGroupItems: (
    orderedItems: GroupItem[],
    allItems: GroupItem[],
  ) => GroupItem[];
  sortSortItems: (orderedItems: SortItem[], allItems: SortItem[]) => SortItem[];
  // Utils
  checkForMissingFields: () => Interfaces.QueryField[];
  buildGraphOptions: () => GraphOptions;
}

const useGraphBuilder = (): useGraphBuilderResult => {
  const dispatch = useDispatch();

  const { graph, graphAccessLevel } = useGraph();
  const { replaceDatasetFilters, filterFieldToConditionalFieldMap } =
    useFilter();

  const datasetMetasState = useSelector(
    (state: RootState) => state.datasetMetas,
  );
  const datasetMetas: DatasetMetaItemOutput[] = datasetMetasState.data.data;

  const [graphOptions, setGraphOptions] = useState<GraphOptions>(
    INITIAL_QUERY_OPTIONS,
  );
  const [groupLength, setGroupLength] = useState<number>(
    graph?.queryParams?.groups?.length || 0,
  );

  /**
   * Sorts group items based on their appearance in the Graph
   *
   * @param {GroupItem[]} orderedItems - Selected groups to retain the order of
   * @param {GroupItem[]} allItems - All available groups
   *
   * @returns {GroupItem[]} Sorted group items
   */
  const sortGroupItems = (
    orderedItems: GroupItem[] = [],
    allItems: GroupItem[],
  ): GroupItem[] => {
    const orderArr = orderedItems.concat(
      allItems.filter(
        (item) =>
          !orderedItems.find((graphGroup) => graphGroup.alias === item.alias),
      ),
    );

    return allItems
      .slice()
      .sort(
        (a, b) =>
          orderArr.findIndex((go) => go.field === a.field) -
          orderArr.findIndex((go) => go.field === b.field),
      );
  };

  /**
   * Sorts sort items based on their appearance in the Graph
   *
   * @param {SortItem[]} orderedItems - Selected sorts to retain the order of
   * @param {SortItem[]} allItems - All available sorts
   *
   * @returns {SortItem[]} Sorted group items
   */
  const sortSortItems = (
    orderedItems: SortItem[] = [],
    allItems: SortItem[],
  ): SortItem[] => {
    const orderArr = orderedItems.concat(
      allItems.filter(
        (item) =>
          !orderedItems.find((graphSort) => graphSort.alias === item.alias),
      ),
    );

    return allItems
      .slice()
      .sort(
        (a, b) =>
          orderArr.findIndex((go) => go.field === a.field) -
          orderArr.findIndex((go) => go.field === b.field),
      );
  };

  /**
   * Checks that all fields in a Graph exist in their respective DatasetMetas
   *
   * @returns {Interfaces.QueryField[]} Array of missing fields
   */
  const checkForMissingFields = useCallback((): Interfaces.QueryField[] => {
    if (!graph?._id) {
      return [];
    }

    return (graph?.queryParams?.fields || []).reduce(
      (acc: Interfaces.QueryField[], graphField) => {
        const datasetDetails = graph.additionalDetails?.find(
          (detail) => detail.datasetMetaId === graphField.datasetMetaId,
        );

        const schemaField = datasetDetails?.schemaData.find(
          (sf) => sf.fieldId === graphField.field,
        );

        if (schemaField) {
          return acc;
        }

        return [...acc, graphField];
      },
      [],
    );
  }, [graph?._id, graph?.additionalDetails, graph?.queryParams?.fields]);

  /**
   * Builds graph options from available fields present in Graph
   *
   * {GraphOptions} Available graph options
   */
  const buildGraphOptions = useCallback((): GraphOptions => {
    if (!graph?._id) {
      return INITIAL_QUERY_OPTIONS;
    }

    const isMultiple =
      graph?.queryParams?.aggregationType === Enums.AggregationType.MULTIPLE;

    const fields = graph?.queryParams?.fields;
    if (!fields?.length) {
      return INITIAL_QUERY_OPTIONS;
    }

    const uniqueFields = {}; // Use an object to track unique fields

    const unorderedOptions = fields.reduce((acc: GraphOptions, graphField) => {
      const dsm = datasetMetas.find(
        (d) => d.entity._id === graphField.datasetMetaId,
      )?.entity;

      if (!dsm) {
        return acc;
      }

      const collection = dsm.dataCollections?.find(
        (c) => c._id === dsm.activeDataCollection,
      );

      const schemaData = collection?.schemaData;

      // Update filters
      if (graph?.queryParams?.filters?.length) {
        const graphFilters =
          { ...graph?.queryParams }?.filters?.map((filterField) =>
            filterFieldToConditionalFieldMap(
              filterField as Interfaces.StandardFilterField,
            ),
          ) || [];

        replaceDatasetFilters(graph._id, graphFilters);
      }

      // Build graph options
      const itemResult = {
        fieldItems: [...acc.fieldItems],
        groupItems: [...acc.groupItems],
        sortItems: [...acc.sortItems],
        displayOrderItems: [...acc.displayOrderItems],
        listIds: [...acc.listIds],
      };

      const schemaField = schemaData
        ?.filter((f) => !(f as unknown as Record<string, unknown>).isMeasure)
        .find(
          (f) => f.fieldId === graphField.field || f.name === graphField.field,
        );

      if (schemaField && !uniqueFields[graphField.field]) {
        // Check if the field is already in uniqueFields
        uniqueFields[graphField.field] = true; // Mark the field as used
        itemResult.fieldItems.push({
          field: schemaField.name,
          fieldId: schemaField.fieldId,
          dataValidation: schemaField.dataValidation,
          datasetMetaName: dsm.name,
          datasetMetaId: graphField.datasetMetaId,
          alias:
            isMultiple && graphField?.alias === schemaField.name
              ? `${schemaField.name} - ${dsm.name}`
              : graphField.alias,
        } as GraphFieldWithDsmName);

        const listValues = schemaField.dataValidation?.constraints?.listValues;

        if (listValues && !Array.isArray(listValues)) {
          itemResult.listIds.push(listValues);
        }
      }

      const existingGroup = itemResult.groupItems.find(
        (group) =>
          group.field === graphField.field &&
          group.datasetMetaId === graphField.datasetMetaId,
      );

      if (!existingGroup) {
        itemResult.groupItems.push({
          field: graphField.field,
          alias: graphField.alias || graphField.field,
          datasetMetaId: graphField.datasetMetaId,
          direction:
            graph.queryParams?.groups?.find(
              (g) =>
                g.field === graphField.field &&
                g.datasetMetaId == graphField.datasetMetaId,
            )?.direction || Enums.ProjectionSortOrder.ASCENDING,
          active: !!graph.queryParams?.groups?.find(
            (gr) =>
              gr.field === graphField.field &&
              gr.datasetMetaId == graphField.datasetMetaId,
          ),
        });
      }

      const existingSort = itemResult.sortItems.find(
        (sort) =>
          sort.field === graphField.field &&
          sort.datasetMetaId === graphField.datasetMetaId &&
          sort.alias === graphField.alias,
      );

      if (!existingSort) {
        itemResult.sortItems.push({
          field: graphField.field,
          alias: graphField.alias || graphField.field,
          datasetMetaId: graphField.datasetMetaId,
          direction:
            graph.queryParams?.sort?.find(
              (s) =>
                s.alias === graphField.alias &&
                s.datasetMetaId == graphField.datasetMetaId,
            )?.direction || Enums.ProjectionSortOrder.ASCENDING,
          active: !!graph.queryParams?.sort?.find(
            (s) =>
              s.alias === graphField.alias &&
              s.datasetMetaId == graphField.datasetMetaId,
          ),
        });
      }

      itemResult.displayOrderItems.push({
        field: graphField.field,
        alias: graphField.alias || graphField.field,
        datasetMetaId: graphField.datasetMetaId,
      } as SortItem);

      return itemResult;
    }, INITIAL_QUERY_OPTIONS);

    return {
      ...unorderedOptions,
      groupItems: sortGroupItems(
        graph?.queryParams?.groups as GroupItem[],
        unorderedOptions.groupItems,
      ),
      sortItems: sortSortItems(
        graph?.queryParams?.sort as SortItem[],
        unorderedOptions.sortItems,
      ),
    };
  }, [
    datasetMetas,
    filterFieldToConditionalFieldMap,
    graph,
    replaceDatasetFilters,
  ]);

  /**
   * Handles a group change interaction
   *
   * @returns {void}
   */
  const handleGroupChange = useCallback(
    (items: GroupItem[] = []) => {
      const clonedItems = cloneDeep(items);
      const cloned = cloneDeep(graph);

      if (groupLength === 0 && cloned.queryParams.supercolumns.length) {
        cloned.queryParams.supercolumns = [];
        setGroupLength(cloned.queryParams.groups.length);
      }

      cloned.queryParams.groups = clonedItems
        .filter((g) => g.active)
        .map((g) => {
          delete g.active;
          if (!g.alias) {
            g.alias = g.field;
          }
          return g;
        });

      if (!cloned.queryParams.groups.length) {
        // Remove all fields if no group length
        cloned.queryParams.supercolumns = [];

        cloned.queryParams.sort = cloned.queryParams.sort.filter(
          (s) => !s.isMeasure,
        );

        // Add all fields to displayOrder
        cloned.queryParams.displayOrder = graphOptions.displayOrderItems;
      } else {
        // Remove non-usable sorts
        cloned.queryParams.sort = cloned.queryParams.sort.filter((sort) =>
          sort.isMeasure
            ? true
            : !!cloned.queryParams.groups.find(
                (g) => g.alias === sort.alias || g.field === sort.field,
              ),
        );

        // Remove any supercolumns that are don't have a matching alias in the groups array
        const groupsAliasMap = cloned.queryParams.groups.map(
          (group) => group.alias,
        );
        const groupsFieldsMap = cloned.queryParams.groups.map(
          (group) => group.field,
        );

        const measureMap = cloned.queryParams.measures.map(
          (measure) => measure.alias,
        );

        const groupsAliasAndFields = groupsAliasMap.concat(
          groupsFieldsMap,
          measureMap,
        );

        cloned.queryParams.supercolumns =
          cloned.queryParams.supercolumns?.filter((supercolumn) => {
            if (
              supercolumn.textTransformation &&
              supercolumn.textTransformation.params.fields
            ) {
              const textTransformationFieldMap =
                supercolumn.textTransformation.params.fields.map(
                  (field) => field,
                );
              if (
                textTransformationFieldMap.length === 0 ||
                textTransformationFieldMap.every((alias) =>
                  groupsAliasAndFields.includes(alias),
                )
              ) {
                return true;
              }
            }
            if (supercolumn.formula) {
              const formulaFieldMap = supercolumn.formula.map(
                (formula) => formula.value.field,
              );

              if (
                formulaFieldMap.length === 0 ||
                formulaFieldMap.some((alias) =>
                  groupsAliasAndFields.includes(alias),
                )
              ) {
                return true;
              }
            }

            return (
              groupsAliasAndFields.includes(
                supercolumn.textTransformation?.params?.field,
              ) ||
              groupsAliasAndFields.includes(
                supercolumn.dateConversion?.params?.field,
              ) ||
              groupsAliasAndFields.includes(supercolumn.field) ||
              groupsAliasAndFields.includes(supercolumn.alias)
            );
          });

        // Add in any new groups to displayOrder and remove any non-usable ones
        cloned.queryParams.displayOrder = [
          ...(cloned.queryParams.displayOrder || []),
          ...cloned.queryParams.groups
            .filter(
              (groupItem) =>
                !cloned.queryParams.displayOrder?.find(
                  (displayOrderItem) =>
                    displayOrderItem.alias === groupItem.alias,
                ),
            )
            .map((groupItem) => ({
              alias: groupItem.alias,
              field: groupItem.field,
              datasetMetaId: groupItem.datasetMetaId,
            })),
        ].filter(
          (displayOrder) =>
            !!cloned.queryParams.measures.find(
              (m) => m.alias === (displayOrder.alias || displayOrder.field),
            ) ||
            !!cloned.queryParams.groups.find(
              (g) =>
                g.alias === (displayOrder.alias || displayOrder.field) ||
                g.field === displayOrder.field,
            ) ||
            !!cloned.queryParams.supercolumns.find(
              (m) => m.alias === (displayOrder.alias || displayOrder.field),
            ),
        );
      }

      dispatch(
        fetchGraphSuccess({
          accessLevel: graphAccessLevel || Enums.AccessLevel.MANAGE,
          entity: cloned,
        }),
      );
    },
    [
      dispatch,
      groupLength,
      graph,
      graphAccessLevel,
      graphOptions.displayOrderItems,
    ],
  );

  /**
   * Handles a sort change interaction
   *
   * @returns {void}
   */
  const handleSortChange = useCallback(
    (items: SortItem[] = []) => {
      const clonedItems = cloneDeep(items);
      const cloned = cloneDeep(graph);

      cloned.queryParams.sort = clonedItems
        .filter((s) => s.active)
        .map((s) => {
          delete s.active;
          return s;
        });

      dispatch(
        fetchGraphSuccess({
          accessLevel: graphAccessLevel || Enums.AccessLevel.MANAGE,
          entity: cloned,
        }),
      );
    },
    [dispatch, graph, graphAccessLevel],
  );

  /**
   * Handles a subtotal change interaction
   *
   * @returns {void}
   */
  const handleSubtotalChange = useCallback(
    (checked: boolean) => {
      const cloned = cloneDeep(graph);

      cloned.queryParams.includeGrandTotals = false;
      cloned.queryParams.includeSubtotals = checked;

      // If checked remove sorts
      if (checked) {
        cloned.queryParams.sort = [];
        cloned.queryParams.groups.map((group) => {
          group.direction = Enums.ProjectionSortOrder.DESCENDING;
          return group;
        });
      }

      dispatch(
        fetchGraphSuccess({
          accessLevel: graphAccessLevel || Enums.AccessLevel.MANAGE,
          entity: cloned,
        }),
      );
    },
    [dispatch, graph, graphAccessLevel],
  );

  /**
   * Handles a total change interaction
   *
   * @returns {void}
   */
  const handleTotalChange = useCallback(
    (checked: boolean) => {
      const cloned = cloneDeep(graph);

      cloned.queryParams.includeSubtotals = false;
      cloned.queryParams.includeGrandTotals = checked;

      // If checked remove sorts
      if (checked) {
        cloned.queryParams.sort = [];
        cloned.queryParams.groups.map((group) => {
          group.direction = Enums.ProjectionSortOrder.DESCENDING;
          return group;
        });
      }

      dispatch(
        fetchGraphSuccess({
          accessLevel: graphAccessLevel || Enums.AccessLevel.MANAGE,
          entity: cloned,
        }),
      );
    },
    [dispatch, graph, graphAccessLevel],
  );

  /**
   * Handles a display order change interaction
   *
   * @returns {void}
   */
  const handleDisplayOrderChange = useCallback(
    (items: Interfaces.SortField[] = []) => {
      const clonedItems = cloneDeep(items);
      const cloned = cloneDeep(graph);

      cloned.queryParams.displayOrder = clonedItems.map((g) => {
        return {
          alias: g.alias || g.field,
          field: g.alias,
          datasetMetaId: g.datasetMetaId,
        };
      });

      dispatch(
        fetchGraphSuccess({
          accessLevel: graphAccessLevel || Enums.AccessLevel.MANAGE,
          entity: cloned,
        }),
      );
    },
    [dispatch, graph, graphAccessLevel],
  );

  useEffect(() => {
    setGraphOptions(buildGraphOptions());
  }, [buildGraphOptions]);

  return {
    graphOptions,
    // Helpers
    handleGroupChange,
    handleSortChange,
    handleSubtotalChange,
    handleTotalChange,
    handleDisplayOrderChange,
    // Sorters
    sortGroupItems,
    sortSortItems,
    // Utils
    checkForMissingFields,
    buildGraphOptions,
  };
};

export default useGraphBuilder;
